// ApprovalsTab — Phase B approvals inbox
// Lists write plans a standing goal drafted but did not apply automatically
// (authorization_policy.mode != auto_within_bounds, or a plan that fell
// outside its bounds). Approve replays the stored plan verbatim (provenance
// re-validated at execution); reject discards it. See U-2 in PROJECT_STATUS.md.

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listApprovals, decideApproval, type PendingApproval } from '../../api/approvals';
import { useCanWriteAgent } from '../../hooks/useCanWriteAgent';
import agentStyles from './AgentChat.module.css';
import styles from './ApprovalsTab.module.css';

const SERVICE_ICONS: Record<string, string> = {
  analytics: '📊', monitoring: '👀', briefing: '📝',
  similarity: '🔍', clustering: '🧩', wordcloud: '☁️',
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function ApprovalCard({ approval, canWrite, onDecided }: { approval: PendingApproval; canWrite: boolean; onDecided: () => void }) {
  const [rememberBounds, setRememberBounds] = useState(false);
  const [deciding, setDeciding] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const plan = approval.proposed_plan;
  const creditEstimate = plan?.total_estimated_credits;

  async function handleDecide(decision: 'approve' | 'reject') {
    setDeciding(decision);
    setError(null);
    try {
      await decideApproval(approval.id, decision, decision === 'approve' && rememberBounds);
      onDecided();
    } catch (e) {
      setError((e as Error).message || `Failed to ${decision} plan.`);
      setDeciding(null);
    }
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <span className={styles.cardTitle}>{approval.goal_title || approval.task_name || 'Scheduled task'}</span>
          <span className={styles.cardTime}>{timeAgo(approval.created_at)}</span>
        </div>
        {plan && creditEstimate == null ? (
          <span className={agentStyles.creditBadgePaid}>💳 Credit cost not yet known</span>
        ) : creditEstimate != null && creditEstimate > 0 ? (
          <span className={agentStyles.creditBadgePaid}>
            💳 {creditEstimate} credit{creditEstimate !== 1 ? 's' : ''}
          </span>
        ) : plan ? (
          <span className={agentStyles.creditBadgeFree}>✓ Free</span>
        ) : null}
      </div>

      {plan && plan.steps.length > 0 ? (
        <ul className={agentStyles.planSteps}>
          {plan.steps.map(step => (
            <li key={step.step_number} className={agentStyles.planStep}>
              <span className={agentStyles.planStepNum}>{step.step_number}</span>
              <span className={agentStyles.planStepDesc}>{step.description}</span>
              <span className={`${agentStyles.planServiceBadge} ${agentStyles[`svc_${step.service}`] || ''}`}>
                {SERVICE_ICONS[step.service] || '⚙️'} {step.service}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.noPlan}>No plan steps were recorded for this run.</p>
      )}

      {error && <p className={styles.error}>{error}</p>}

      {canWrite ? (
        <div className={styles.cardActions}>
          <label className={styles.rememberCheckbox} title="Owner/admin only — lets this goal auto-apply the same tools next time">
            <input
              type="checkbox"
              checked={rememberBounds}
              onChange={e => setRememberBounds(e.target.checked)}
              disabled={deciding !== null}
            />
            Approve &amp; remember bounds
          </label>
          <div className={styles.cardActionsRight}>
            <button
              className={styles.rejectBtn}
              onClick={() => handleDecide('reject')}
              disabled={deciding !== null}
            >
              {deciding === 'reject' ? 'Rejecting…' : 'Reject'}
            </button>
            <button
              className={styles.approveBtn}
              onClick={() => handleDecide('approve')}
              disabled={deciding !== null}
            >
              {deciding === 'approve' ? 'Applying…' : 'Approve →'}
            </button>
          </div>
        </div>
      ) : (
        <p className={styles.readOnlyNote}>Read-only — an owner or admin can approve or reject this plan.</p>
      )}
    </div>
  );
}

export default function ApprovalsTab() {
  const qc = useQueryClient();
  const canWrite = useCanWriteAgent();

  const { data: approvals, isLoading, error } = useQuery({
    queryKey: ['approvals'],
    queryFn: () => listApprovals(),
    refetchInterval: 30_000,
  });

  function handleDecided() {
    qc.invalidateQueries({ queryKey: ['approvals'] });
    qc.invalidateQueries({ queryKey: ['goals'] });
    qc.invalidateQueries({ queryKey: ['goal-runs'] });
  }

  return (
    <div className={styles.container}>
      <div className={styles.sectionHeader}>
        <p className={styles.sectionLabel}>Pending Approvals</p>
        {approvals && approvals.length > 0 && (
          <span className={styles.countBadge}>{approvals.length}</span>
        )}
      </div>

      {isLoading && <p className={styles.emptyText}>Loading approvals…</p>}
      {error && <p className={styles.errorText}>Could not load approvals.</p>}

      {!isLoading && !error && (!approvals || approvals.length === 0) && (
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}>✅</span>
          <p className={styles.emptyText}>Nothing waiting on you.</p>
          <p className={styles.emptySubtext}>
            When a standing goal drafts a write it can&apos;t apply on its own, it shows up here for a one-click decision.
          </p>
        </div>
      )}

      {approvals && approvals.length > 0 && (
        <div className={styles.list}>
          {approvals.map(a => (
            <ApprovalCard key={a.id} approval={a} canWrite={canWrite} onDecided={handleDecided} />
          ))}
        </div>
      )}
    </div>
  );
}
